/* eslint-disable import/prefer-default-export */
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackRudderstackImageUploadError } from "common/utils/rudderstack/tracker";
import { getAcceptString, materializeFile, validateArtworkFileType } from "./artwork-file-types";

/**
 * Checks if the file type is supported for printing.
 *
 * @param {File} file - The file to be checked.
 * @param {boolean} printAsIs - Indicates whether to print the file as is (gangsheet) or not (artwork).
 * @returns {Object} - An object containing `valid` (boolean) and `message` (string) properties.
 */
export const isFileTypeSupported = (file, printAsIs) => {
  const fileChecker = printAsIs ? gangsheetFileChecker : validateArtworkFileType;

  return fileChecker(file);
};

/**
 * Validates file type and materializes the file for upload.
 * Handles files from cloud storage providers (Google Drive, Dropbox) on mobile
 * by reading file content into memory before upload.
 *
 * @param {File} file - The file to validate and materialize.
 * @param {boolean} printAsIs - Indicates whether to print the file as is (gangsheet) or not (artwork).
 * @returns {Promise<{ valid: boolean, message: string, errorType?: string, file?: File }>} - Validation result with materialized file.
 */
export const validateAndMaterializeFile = async (file, printAsIs) => {
  // First check file type (quick check, no file read needed)
  const typeValidation = isFileTypeSupported(file, printAsIs);
  if (!typeValidation.valid) {
    return { ...typeValidation, errorType: "unsupported_type" };
  }

  // Check file size before reading the file (must match backend DtfArtwork constants)
  const maxSizeBytes = printAsIs ? 500 * 1024 * 1024 : 40 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    const maxMb = maxSizeBytes / (1024 * 1024);
    return {
      valid: false,
      message: `File is too large. Maximum allowed size is ${maxMb} MB.`,
      errorType: "file_too_large"
    };
  }

  // Materialize the file to handle cloud storage providers on mobile
  // This ensures the file content is actually available before upload
  const { file: materializedFile, error: materializeError } = await materializeFile(file);
  if (materializeError || !materializedFile) {
    return {
      valid: false,
      message: materializeError || "Failed to read file",
      errorType: "materialization_error"
    };
  }

  // Re-check size after materialization — cloud-provider files (Google Drive, Dropbox)
  // on mobile may report file.size as 0 or a stub value before content is downloaded
  if (materializedFile.size > maxSizeBytes) {
    const maxMb = maxSizeBytes / (1024 * 1024);
    return {
      valid: false,
      message: `File is too large. Maximum allowed size is ${maxMb} MB.`,
      errorType: "file_too_large"
    };
  }

  // Return the materialized file so the caller can use it for upload
  return { valid: true, message: "", file: materializedFile };
};
/* eslint-enable import/prefer-default-export */

const gangsheetFileChecker = ({ type, name }) => {
  // Check MIME type first, then fallback to extension for Android cloud provider files
  let valid = type === "image/png";

  if (!valid && !type && name) {
    const extension = name.toLowerCase().match(/\.[^.]+$/)?.[0];
    valid = extension === ".png";
  }

  const message = valid ? "" : "PNG file is required for gang sheet orders.";

  return { valid, message };
};

/**
 * Returns the accepted file types based on the given printAsIs flag.
 *
 * @param {boolean} printAsIs - Indicates whether to print the file as is (gangsheet) or not (artwork).
 * @returns {string} - The accepted file types.
 */
export const acceptedFileTypes = printAsIs => (printAsIs ? "image/png" : getAcceptString());

export const logUnsupportedFileTypeEvent = (file, otherData, errorMessage) => {
  submitDtfEvent("image_upload_unsupported_file", {
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    ...otherData
  });
  trackRudderstackImageUploadError({
    error_message: errorMessage,
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    ...otherData
  });
};
